import { DatePipe } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { PasswordModule } from 'primeng/password';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { AppConfigurator } from '../../../layout/component/app.configurator';
import { LinkSenha, SENHA_MAXIMA, SENHA_MINIMA, SenhaService } from '../../../services/senha.service';
import { ToastService } from '../../../services/toast.service';

/** Confere se a confirmação é igual à nova senha. */
export function senhasIguais(grupo: AbstractControl): ValidationErrors | null {
    const nova = grupo.get('novaSenha')?.value;
    const confirmacao = grupo.get('confirmacao')?.value;
    return nova && confirmacao && nova !== confirmacao ? { senhasDiferentes: true } : null;
}

/**
 * Página aberta pelo link do e-mail (convite de usuário novo ou redefinição pedida pelo administrador).
 * Pública: quem vale é o token do link, que vai no corpo das requisições.
 */
@Component({
    selector: 'app-redefinir-senha',
    imports: [DatePipe, ReactiveFormsModule, RouterModule, ButtonModule, MessageModule, PasswordModule, ProgressSpinnerModule, AppConfigurator],
    templateUrl: './redefinir-senha.component.html'
})
export class RedefinirSenhaComponent implements OnInit {
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private fb = inject(FormBuilder);
    private senhas = inject(SenhaService);
    private toast = inject(ToastService);

    readonly minimo = SENHA_MINIMA;
    readonly estado = signal<'carregando' | 'valido' | 'invalido'>('carregando');
    readonly link = signal<LinkSenha | null>(null);
    salvando = false;
    erro = '';
    private token = '';

    readonly form = this.fb.nonNullable.group({
        novaSenha: ['', [Validators.required, Validators.minLength(SENHA_MINIMA), Validators.maxLength(SENHA_MAXIMA)]],
        confirmacao: ['', Validators.required]
    }, { validators: senhasIguais });

    ngOnInit(): void {
        this.token = this.route.snapshot.queryParamMap.get('token') ?? '';
        if (!this.token) { this.estado.set('invalido'); return; }
        this.senhas.consultarLink(this.token).subscribe({
            next: link => { this.link.set(link); this.estado.set('valido'); },
            error: () => this.estado.set('invalido')
        });
    }

    get convite(): boolean {
        return this.link()?.tipo === 'CONVITE';
    }

    invalido(campo: 'novaSenha' | 'confirmacao'): boolean {
        const controle = this.form.get(campo)!;
        return controle.invalid && controle.touched;
    }

    salvar(): void {
        this.form.markAllAsTouched();
        if (this.form.invalid) return;
        this.salvando = true;
        this.erro = '';
        this.senhas.definirPeloLink(this.token, this.form.getRawValue().novaSenha).subscribe({
            next: () => {
                this.salvando = false;
                this.toast.sucesso('Senha definida', 'Entre com o seu e-mail e a nova senha.');
                this.router.navigate(['/auth/login']);
            },
            error: e => {
                this.salvando = false;
                if (e?.status === 404) { this.estado.set('invalido'); return; }
                this.erro = e?.error?.message || 'Não foi possível definir a senha. Tente novamente.';
            }
        });
    }
}
